import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import './OrderCard.css';
import { completeWorkOrder, deleteWorkOrder, sendWorkOrder, updateWorkOrder } from '../../api';
import { MaterialTypesContext } from '../../context/MaterialTypesContext';
import { formatMaterialSize, formatMeasurement } from '../../materials/materialSize';
import {
  canComplete, canEditMachineMemo, canEditPayload, canSend, dispatchLabel, effectiveDispatchState,
  isCompleted, isUnknownAttemptState, machineOptionKey, needsResend, parseMachineOptionKey, selectedMachineKey,
} from './workOrderLifecycle';

const printTextEscapes = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const escapePrintText = (value) => String(value ?? '').replace(/[&<>"']/g, (character) => printTextEscapes[character]);

function OrderCard({ order, machineOptions, liveMachines, machineList, refreshData = () => {} }) {
  const { resolveMaterialType, resolveMaterialShape = (value) => value || '-' } = useContext(MaterialTypesContext);
  const immutableOptions = useMemo(() => machineOptions || [], [machineOptions]);
  const statuses = useMemo(() => liveMachines || machineList || [], [liveMachines, machineList]);
  const [memo, setMemo] = useState(order.memo || '');
  const [selectedKey, setSelectedKey] = useState(selectedMachineKey(order));
  const [completedQuantity, setCompletedQuantity] = useState('');
  const [pendingAction, setPendingAction] = useState('');
  const pendingActionRef = useRef('');
  const memoDirtyRef = useRef(false);
  const memoPendingRef = useRef(false);
  const machinePendingRef = useRef(false);
  const lastOrderIDRef = useRef(order.id);
  const latestMemoRef = useRef(order.memo || '');
  const latestMachineKeyRef = useRef(selectedMachineKey(order));
  latestMemoRef.current = order.memo || '';
  latestMachineKeyRef.current = selectedMachineKey(order);

  useEffect(() => {
    const identityChanged = lastOrderIDRef.current !== order.id;
    const terminal = order.status === '완료';
    if (identityChanged || terminal) {
      memoDirtyRef.current = false;
      memoPendingRef.current = false;
      machinePendingRef.current = false;
      setMemo(latestMemoRef.current);
      setSelectedKey(latestMachineKeyRef.current);
      setCompletedQuantity('');
    } else {
      if (!memoDirtyRef.current && !memoPendingRef.current) setMemo(latestMemoRef.current);
      if (!machinePendingRef.current) setSelectedKey(latestMachineKeyRef.current);
    }
    lastOrderIDRef.current = order.id;
  }, [order.id, order.version, order.status, order.memo, order.machineKind, order.machineRef]);

  const selectedOption = useMemo(
    () => immutableOptions.find((machine) => machineOptionKey(machine) === selectedKey),
    [immutableOptions, selectedKey],
  );
  const projectedOrder = { ...order, ...(parseMachineOptionKey(selectedKey) || { machineKind: '', machineRef: '' }) };
  const selectedName = selectedKey === selectedMachineKey(order)
    ? (order.machineName || selectedOption?.machineName || '미지정')
    : (selectedOption?.machineName || '미지정');
  const appliedName = order.lastSentMachineName || (effectiveDispatchState(order) === 'sent' ? order.machineName : '') || '미적용';
  const locked = Boolean(pendingAction);
  const editableMachineMemo = canEditMachineMemo(order);
  const completed = isCompleted(order);
  const manualDevice = projectedOrder.machineKind === 'manual';
  const resolvedMaterialShape = resolveMaterialShape(order.materialShapeCode ?? order.materialShape, order.materialShape);
  const materialSizeText = formatMaterialSize(
    order.materialShapeCode, resolvedMaterialShape, order.materialWidth, order.materialHeight,
  );

  const runMutation = async (name, mutation, failureMessage) => {
    if (pendingActionRef.current) return false;
    pendingActionRef.current = name;
    setPendingAction(name);
    try {
      await mutation();
      await refreshData();
      return true;
    } catch (error) {
      window.alert(`${failureMessage}: ${error.message}`);
      try { await refreshData(); } catch (_) { /* retain original error feedback */ }
      return false;
    } finally {
      pendingActionRef.current = '';
      setPendingAction('');
    }
  };

  const handleMachineChange = async (event) => {
    if (pendingActionRef.current) return;
    const nextKey = event.target.value;
    machinePendingRef.current = true;
    setSelectedKey(nextKey);
    const identity = parseMachineOptionKey(nextKey) || { machineKind: '', machineRef: '' };
    const succeeded = await runMutation(
      'machine',
      () => updateWorkOrder(order.id, { expectedVersion: order.version, ...identity }),
      '장비 저장 실패',
    );
    machinePendingRef.current = false;
    if (!succeeded) setSelectedKey(latestMachineKeyRef.current);
  };

  const handleMemoBlur = async () => {
    if (!editableMachineMemo || locked) return;
    if (memo === latestMemoRef.current) {
      memoDirtyRef.current = false;
      return;
    }
    memoPendingRef.current = true;
    const succeeded = await runMutation(
      'memo',
      () => updateWorkOrder(order.id, { expectedVersion: order.version, memo }),
      '비고 저장 실패',
    );
    memoPendingRef.current = false;
    memoDirtyRef.current = false;
    if (!succeeded) setMemo(latestMemoRef.current);
  };

  const handleUrgentToggle = () => runMutation(
    'urgent',
    () => updateWorkOrder(order.id, { expectedVersion: order.version, urgent: !order.urgent }),
    '긴급 설정 실패',
  );

  const handleSend = async () => {
    const retryUnknownAttempt = isUnknownAttemptState(order);
    if (retryUnknownAttempt && !window.confirm('전송 결과를 알 수 없습니다. 재시도하면 장비에 작업이 중복 적용될 위험이 있습니다. 결과를 확인했으며 그래도 재시도하시겠습니까?')) return;
    await runMutation(
      'send',
      () => sendWorkOrder(order.id, order.version, retryUnknownAttempt),
      retryUnknownAttempt ? '재시도 실패' : '전송 실패',
    );
  };

  const handleComplete = async () => {
    let quantity;
    let quantityMessage = `현재 생산수량 ${order.workNum || 0}개로`;
    if (manualDevice) {
      const rawQuantity = completedQuantity.trim();
      if (rawQuantity !== '') {
        quantity = Number(rawQuantity);
        if (!Number.isSafeInteger(quantity) || quantity < 0 || quantity > Number(order.inputNum)) {
          window.alert(`완료 수량은 0부터 목표 수량 ${order.inputNum} 사이의 정수여야 합니다.`);
          return;
        }
        quantityMessage = `완료 수량 ${quantity}개로`;
      } else {
        quantityMessage = `완료 수량을 목표 수량 ${order.inputNum}개로`;
      }
    }
    if (!window.confirm(`${quantityMessage} 완료 작업기록을 생성합니다.\n작업지시서는 전송됨 상태로 유지됩니다. 계속하시겠습니까?`)) return;
    await runMutation(
      'complete',
      () => quantity === undefined
        ? completeWorkOrder(order.id, order.version)
        : completeWorkOrder(order.id, order.version, quantity),
      '완료 처리 실패',
    );
  };

  const handleDelete = async () => {
    if (!window.confirm('정말 이 작업 지시를 삭제하시겠습니까?\n삭제 기록은 이력 로그에 안전하게 보관됩니다.')) return;
    await runMutation('delete', () => deleteWorkOrder(order.id), '작업 지시 삭제 실패');
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=450,height=450');
    if (!printWindow) {
      window.alert('팝업 차단을 해제해주세요.');
      return;
    }
    const values = {
      id: escapePrintText(order.id ? order.id.slice(-8).toUpperCase() : '-'),
      job: escapePrintText(order.jobName), machine: escapePrintText(selectedName),
      printedAt: escapePrintText(new Date().toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false })),
      material: escapePrintText(resolveMaterialType(order.materialTypeCode ?? order.materialType, order.materialType)),
      shape: escapePrintText(resolvedMaterialShape),
      size: escapePrintText(materialSizeText),
      length: escapePrintText(formatMeasurement(order.inputLength)),
      count: escapePrintText(order.inputNum), memo: escapePrintText(order.memo || '-'),
    };
    printWindow.document.write(`
      <html>
        <head>
          <title>작업 지시서</title>
          <style>
            @page {
              size: 100mm 100mm;
              margin: 2mm;
            }
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            html, body {
              width: 96mm;
              height: 96mm;
              margin: 0;
              padding: 0;
              font-family: 'Malgun Gothic', '맑은 고딕', Arial, sans-serif;
              color: #000;
              background-color: #fff;
              overflow: hidden;
            }
            .receipt {
              width: 96mm;
              height: 96mm;
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              border: none;
              padding: 1mm;
              box-sizing: border-box;
            }
            .title {
              text-align: center;
              font-size: 28px;
              font-weight: bold;
              margin-bottom: 0.5mm;
              border-bottom: 4px double #000;
              padding-bottom: 1mm;
              letter-spacing: 2px;
            }
            .content-grid {
              display: flex;
              justify-content: space-between;
              height: auto;
              margin-bottom: 0.5mm;
            }
            .col {
              width: 48%;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
            }
            .row {
              display: flex;
              flex-direction: column;
              border-bottom: 1.5px solid #000;
              padding-bottom: 1px;
              margin-bottom: 0.5mm;
            }
            .label {
              font-size: 13px;
              color: #000;
              font-weight: bold;
              line-height: 1.1;
            }
            .value {
              font-size: 20px;
              font-weight: bold;
              white-space: normal;
              word-break: break-all;
              line-height: 1.1;
            }
            .memo-container {
              margin-top: 1mm;
            }
            .memo-label {
              font-size: 13px;
              color: #000;
              font-weight: bold;
            }
            .memo-box {
              border: 2px solid #000;
              padding: 1.5mm;
              font-size: 18px;
              font-weight: bold;
              min-height: 11mm;
              max-height: 14mm;
              overflow: hidden;
              word-break: break-all;
              line-height: 1.2;
            }
            .footer {
              text-align: center;
              font-size: 11px;
              margin-top: 0.5mm;
              border-top: 1.5px solid #000;
              padding-top: 1mm;
              color: #000;
              font-weight: bold;
            }
            @media print {
              html, body {
                width: 96mm !important;
                height: 96mm !important;
                margin: 0 !important;
                padding: 0 !important;
                overflow: hidden !important;
              }
              .receipt {
                width: 96mm !important;
                height: 94mm !important;
                box-sizing: border-box !important;
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="receipt">
            <div>
              <div class="title">작업 지시서</div>
              <div class="content-grid">
                <div class="col" style="border-right: 1.5px solid #000; padding-right: 2.5mm;">
                  <div class="row">
                    <span class="label">지시 ID</span>
                    <span class="value" style="font-family: monospace;">${values.id}</span>
                  </div>
                  <div class="row">
                    <span class="label">작업명</span>
                    <span class="value" style="font-size: 20px;">${values.job}</span>
                  </div>
                  <div class="row">
                    <span class="label">할당 장비</span>
                    <span class="value">${values.machine}</span>
                  </div>
                  <div class="row" style="border-bottom: none; margin-bottom: 0;">
                    <span class="label">출력 일시</span>
                    <span class="value" style="font-size: 12px; font-weight: normal;">${values.printedAt}</span>
                  </div>
                </div>

                <div class="col" style="padding-left: 2.5mm;">
                  <div class="row">
                    <span class="label">소재 정보</span>
                    <span class="value" style="font-size: 18px;">${values.material} / ${values.shape}</span>
                  </div>
                  <div class="row">
                    <span class="label">소재 크기</span>
                    <span class="value">${values.size}</span>
                  </div>
                  <div class="row">
                    <span class="label">절단 길이</span>
                    <span class="value">${values.length}</span>
                  </div>
                  <div class="row" style="border-bottom: none; margin-bottom: 0;">
                    <span class="label">목표 수량</span>
                    <span class="value" style="font-size: 24px; color: #000;">${values.count} 개</span>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div class="memo-container">
                <div class="memo-label">비고 (메모)</div>
                <div class="memo-box">${values.memo}</div>
              </div>
              <div class="footer">
                Delta Monitoring System
              </div>
            </div>
          </div>
          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 500);
            }
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const liveLookupName = order.lastSentMachineName || selectedName;
  const liveMessage = statuses.find((machine) => machine.machineName === liveLookupName)?.message || '';
  let statusText = dispatchLabel(order);
  let statusClass = `dispatch-${effectiveDispatchState(order)}`;
  if (completed) { statusText = '완료'; statusClass = 'status-completed'; }
  else if (liveMessage.includes('Alarm') || liveMessage.includes('에러')) statusClass = 'status-alarm';
  else if (liveMessage.includes('자동 운전') || liveMessage.includes('작업 시작') || order.status === '진행중') statusClass = 'status-operating';

  let sendLabel = effectiveDispatchState(order) === 'draft' ? '전송' : '다시 전송';
  if (isUnknownAttemptState(order)) sendLabel = '전송 결과 확인 후 재시도';

  return (
    <div className={`order-card-row ${order.urgent ? 'urgent-row' : ''}`}>
      <div className={`status-sidebar ${statusClass}`} aria-label={`전송 상태 ${statusText}`}>{statusText}</div>
      <div className="order-content">
        <div className="order-main-info">
          <div className="cell job-col"><div className="emphasize-box">{order.jobName}</div></div>
          <div className="cell material-col">{resolveMaterialType(order.materialTypeCode ?? order.materialType, order.materialType)}</div>
          <div className="cell shape-col"><div className="emphasize-box">{resolvedMaterialShape}</div></div>
          <div className="cell size-col">{materialSizeText} mm</div>
          <div className="cell length-col">{formatMeasurement(order.inputLength)} mm</div>
          <div className="cell count-col">{order.inputNum}</div>
          <div className="cell time-col">{order.createdAt ? new Date(order.createdAt).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '-'}</div>
          <div className="cell finish-col">{order.finishedAt && order.finishedAt !== '0001-01-01T00:00:00Z' ? new Date(order.finishedAt).toLocaleString('ko-KR') : '--:--'}</div>
          <div className="cell action-col">
            <label className="visually-hidden" htmlFor={`machine-${order.id}`}>작업 장비</label>
            <select id={`machine-${order.id}`} aria-label="작업 장비" className="machine-select" value={selectedKey} onChange={handleMachineChange} disabled={!editableMachineMemo || locked}>
              <option value="">미지정</option>
              {immutableOptions.filter((m) => m.machineKind === 'connected').length > 0 && <optgroup label="연결 장비">{immutableOptions.filter((m) => m.machineKind === 'connected').map((m) => <option key={machineOptionKey(m)} value={machineOptionKey(m)}>{m.machineName}</option>)}</optgroup>}
              {immutableOptions.filter((m) => m.machineKind === 'manual').length > 0 && <optgroup label="기타 장비">{immutableOptions.filter((m) => m.machineKind === 'manual').map((m) => <option key={machineOptionKey(m)} value={machineOptionKey(m)}>{m.machineName}</option>)}</optgroup>}
            </select>
          </div>
        </div>

        {(needsResend(projectedOrder) || order.dispatchError) && (
          <div className="dispatch-details">
            {needsResend(projectedOrder) && <span className="resend-needed">선택: {selectedName} / 적용: {appliedName} / 재전송 필요</span>}
            {order.dispatchError && <span className="dispatch-error">{order.dispatchError}</span>}
          </div>
        )}

        <div className="order-memo-row">
          <div className="memo-container">
            <label className="memo-label" htmlFor={`memo-${order.id}`}>비고</label>
            <input id={`memo-${order.id}`} aria-label="비고" type="text" className="memo-input" value={memo} maxLength="500" onChange={(event) => { memoDirtyRef.current = true; setMemo(event.target.value); }} onBlur={handleMemoBlur} disabled={!editableMachineMemo || locked} placeholder="비고 내용을 입력하세요..." />
            <div className="order-action-row">
              {canEditPayload(order) && <button type="button" className={`urgent-toggle-btn ${order.urgent ? 'is-urgent' : ''}`} onClick={handleUrgentToggle} disabled={locked}>{order.urgent ? '긴급해제' : '긴급설정'}</button>}
              <button type="button" className="print-btn" onClick={handlePrint} disabled={locked}>지시서 인쇄</button>
              {canSend(projectedOrder) && <button type="button" className="send-btn" onClick={handleSend} disabled={locked || !selectedKey}>{sendLabel}</button>}
              {manualDevice && canComplete(projectedOrder) && <label className="completion-quantity-label">완료 수량<input aria-label="완료 수량" className="completion-quantity-input" type="number" min="0" max={order.inputNum} step="1" value={completedQuantity} onChange={(event) => setCompletedQuantity(event.target.value)} disabled={locked} placeholder={String(order.inputNum)} /></label>}
              {canComplete(order) && <button type="button" className="complete-btn" onClick={handleComplete} disabled={locked}>수동 완료</button>}
              {canEditPayload(order) && <button type="button" className="delete-btn" onClick={handleDelete} disabled={locked}>삭제</button>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OrderCard;
